import { type CSSProperties, type PointerEvent, type ReactElement } from 'react'
import { MOBILE_CONTROL_HEIGHT } from '@theme'
import { getName, type UserProfileData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Box, Card, Center, Divider, Drawer, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { useTranslation } from '@front/model'
import { useSwipeToDismiss } from '../useSwipeToDismiss'
import { ButtonSignOut } from '../signout'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'
import { UserInfo } from '../../entityInfo'
import { useSidebarAccountActions } from './useSidebarAccountActions'
import { SidebarUserMenuItem } from './SidebarUserMenuItem'
import { AppearanceControl } from './AppearanceControl'

const sheetContentStyle: CSSProperties = {
  height: 'auto',
  maxHeight: '85dvh',
  maxWidth: '100%',
  width: '100%',
}

const sheetBodyStyle: CSSProperties = {
  paddingBottom: 'calc(var(--mantine-spacing-md) + env(safe-area-inset-bottom))',
}

const sheetInnerStyle: CSSProperties = {
  left: 0,
  right: 0,
  width: '100%',
}

const stopSheetPointer = (event: PointerEvent<HTMLElement>): void => {
  event.stopPropagation()
}

type MobileAccountTileProps = {
  user?: UserProfileData
  showDetails: boolean
  onNavigate?: VoidFunction
}

export const MobileAccountTile = ({ user, showDetails, onNavigate }: MobileAccountTileProps): ReactElement => {
  const { t } = useTranslation()
  const [opened, { open, close }] = useDisclosure(false)
  const userName = user ? getName(user) : t('Account')

  const closeSheet = (): void => {
    close()
    onNavigate?.()
  }

  return (
    <>
      <UnstyledButton
        aria-expanded={opened}
        aria-haspopup='dialog'
        aria-label={t('Account menu')}
        className='w-full'
        onClick={open}
      >
        <Card mih={MOBILE_CONTROL_HEIGHT} p='sm' radius='xl' withBorder>
          <Group gap='sm' justify={showDetails ? 'space-between' : 'center'} wrap='nowrap'>
            {user && <UserInfo avatarOnly linked={false} size='md' userId={user.userId} />}
            <Box className='min-w-0' flex={1} hidden={!showDetails}>
              <Text className='break-words' fw='bold'>
                {userName}
              </Text>
              {user?.email && (
                <Text c='dimmed' fz='sm' truncate>
                  {user.email}
                </Text>
              )}
            </Box>
            <Box className='shrink-0' hidden={!showDetails}>
              <MaterialSymbolIcon icon={opened ? 'expand_less' : 'expand_more'} size='lg' />
            </Box>
          </Group>
        </Card>
      </UnstyledButton>
      <MobileAccountSheet opened={opened} user={user} onClose={close} onNavigate={closeSheet} />
    </>
  )
}

const MobileAccountSheet = ({
  opened,
  user,
  onClose,
  onNavigate,
}: {
  opened: boolean
  user?: UserProfileData
  onClose: VoidFunction
  onNavigate: VoidFunction
}): ReactElement => {
  const { t } = useTranslation()
  const account = useSidebarAccountActions({ sidebarUser: user, onNavigate })
  const swipe = useSwipeToDismiss({ direction: 'down', onDismiss: onClose })

  return (
    <Drawer.Root
      aria-label={t('Account')}
      classNames={{ content: 'pointer-events-auto', inner: 'pointer-events-none' }}
      opened={opened}
      padding='md'
      portalProps={{ target: 'body' }}
      position='bottom'
      radius='lg'
      size='auto'
      styles={{ body: sheetBodyStyle, content: sheetContentStyle, inner: sheetInnerStyle }}
      withinPortal
      onClose={onClose}
    >
      <Drawer.Overlay backgroundOpacity={0.45} onPointerDown={stopSheetPointer} />
      <Drawer.Content onPointerDown={stopSheetPointer}>
        <Drawer.Body>
          <Stack gap='sm' {...swipe}>
            <Center>
              <Box aria-hidden bg='gray.4' className='rounded-full' h={4} w={40} />
            </Center>
            <Group align='flex-start' gap='sm' wrap='nowrap'>
              {account.user && <UserInfo avatarOnly linked={false} size='md' userId={account.user.userId} />}
              <Box className='min-w-0' flex={1}>
                <Text fw={700} lineClamp={1}>
                  {account.userName}
                </Text>
                {account.user?.email && (
                  <Text c='dimmed' fz='sm' lineClamp={1}>
                    {account.user.email}
                  </Text>
                )}
              </Box>
            </Group>
            <AppearanceControl fullWidth />
            {account.user && (
              <SidebarUserMenuItem icon='person' label={t('Profile')} tall onClick={account.openProfile} />
            )}
            <SidebarUserMenuItem icon='settings' label={t('Settings')} tall onClick={account.openSettings} />
            <Divider />
            <ButtonSignOut fullWidth h={MOBILE_CONTROL_HEIGHT} icon='logout' justify='start' variant='destructive' />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  )
}
